import React, {
    useEffect,
    useRef,
    useState,
    type FormEvent,
    type ReactNode,
} from 'react';
import { Button } from '@/ui/components/button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/ui/components/dialog';
import { Label } from '@/ui/components/label';
import { cn } from '@/ui/lib/utils';
import { Alert, Field, PasswordField, StrengthMeter } from './components';
import {
    ApiError,
    CHANGE_PASSWORD,
    DELETE_ACCOUNT,
    FORGOT,
    LOGOUT_EVERYWHERE,
    REFRESH,
    REMOVE_AVATAR,
    SET_AVATAR,
    SETTINGS_VIEWER,
    SET_DIETARY,
    UPDATE_PROFILE,
    clearSignedInHint,
    hasSignedInHint,
    messageOf,
    nameOf,
    v2,
} from './api';
import { PhotoError, shrinkPhoto } from './photo';

interface Dietary {
    slug: string;
    name: string;
}

interface SettingsViewer {
    id: string;
    email: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    bio: string | null;
    hasPassword: boolean;
    googleLinked: boolean;
    dietaryPreferences: Dietary[];
}

interface Loaded {
    viewer: SettingsViewer;
    dietaryOptions: Dietary[];
}

type Load =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | ({ status: 'ready' } & Loaded);

const MAX_BIO = 160;
const go = (url: string) => window.location.assign(url);
const toLogin = () =>
    go(`/login?next=${encodeURIComponent('/account/settings')}`);

/** The signed-in account and the dietary options; renews an expired access token once. */
async function loadSettings(): Promise<Loaded | null> {
    if (!hasSignedInHint()) return null;
    const first = await v2<{
        viewer: SettingsViewer | null;
        dietaryOptions: Dietary[];
    }>(SETTINGS_VIEWER);
    if (first.viewer) return first as Loaded;
    await v2(REFRESH);
    const second = await v2<{
        viewer: SettingsViewer | null;
        dietaryOptions: Dietary[];
    }>(SETTINGS_VIEWER);
    return second.viewer ? (second as Loaded) : null;
}

const SECTIONS = [
    ['profile', 'Profile'],
    ['dietary', 'Dietary preferences'],
    ['password', 'Password'],
    ['sessions', 'Signed-in devices'],
] as const;

const Section = ({
    id,
    title,
    description,
    children,
}: {
    id: string;
    title: string;
    description?: string;
    children: ReactNode;
}) => (
    <section
        id={id}
        className="flex scroll-mt-24 flex-col gap-5 border-b border-border-subtle pb-10"
    >
        <div className="flex flex-col gap-1">
            <h2 className="text-title-3 font-semibold text-ink">{title}</h2>
            {description && (
                <p className="text-body-2 text-ink-muted">{description}</p>
            )}
        </div>
        {children}
    </section>
);

const Avatar = ({
    viewer,
    url,
}: {
    viewer: SettingsViewer;
    url: string | null;
}) =>
    url ? (
        <img
            src={url}
            alt=""
            referrerPolicy="no-referrer"
            className="size-20 rounded-full object-cover"
        />
    ) : (
        <span className="flex size-20 items-center justify-center rounded-full bg-primary text-title-1 font-semibold text-primary-foreground">
            {nameOf(viewer).charAt(0).toUpperCase()}
        </span>
    );

/** Upload, replace or remove the profile photo. Saves straight away, apart from the form. */
const PhotoPicker = ({ viewer }: { viewer: SettingsViewer }) => {
    const [url, setUrl] = useState(viewer.avatarUrl);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const input = useRef<HTMLInputElement>(null);

    const run = async (work: () => Promise<string | null>) => {
        setError(null);
        setBusy(true);
        try {
            setUrl(await work());
        } catch (err) {
            setError(err instanceof PhotoError ? err.message : messageOf(err));
        }
        setBusy(false);
    };

    const upload = (file: File) =>
        run(async () => {
            const image = await shrinkPhoto(file);
            const res = await v2<{ setAvatar: { avatarUrl: string | null } }>(
                SET_AVATAR,
                { image },
            );
            return res.setAvatar.avatarUrl;
        });

    const remove = () =>
        run(async () => {
            const res = await v2<{
                removeAvatar: { avatarUrl: string | null };
            }>(REMOVE_AVATAR);
            return res.removeAvatar.avatarUrl;
        });

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-4">
                <Avatar viewer={viewer} url={url} />
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        disabled={busy}
                        onClick={() => input.current?.click()}
                    >
                        {busy ? 'Saving…' : url ? 'Change photo' : 'Add photo'}
                    </Button>
                    {url && (
                        <Button
                            type="button"
                            variant="naked"
                            disabled={busy}
                            onClick={remove}
                        >
                            Remove
                        </Button>
                    )}
                </div>
                <input
                    ref={input}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="sr-only"
                    tabIndex={-1}
                    aria-label="Choose a profile photo"
                    onChange={e => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) upload(file);
                    }}
                />
            </div>
            {error ? (
                <p role="alert" className="text-caption text-warning">
                    {error}
                </p>
            ) : (
                <p className="text-caption text-ink-muted">
                    JPEG, PNG or WebP. We crop it to a square.
                </p>
            )}
        </div>
    );
};

// ─── Profile + dietary (one save, like the design) ────────────────────────────

const ProfileForm = ({ viewer, dietaryOptions }: Loaded) => {
    const [displayName, setDisplayName] = useState(viewer.displayName ?? '');
    const [username, setUsername] = useState(viewer.username);
    const [bio, setBio] = useState(viewer.bio ?? '');
    const [diet, setDiet] = useState<string[]>(
        viewer.dietaryPreferences.map(d => d.slug),
    );
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [status, setStatus] = useState<{
        tone: 'error' | 'success';
        text: string;
    } | null>(null);
    const [busy, setBusy] = useState(false);

    const toggle = (slug: string) =>
        setDiet(d =>
            d.includes(slug) ? d.filter(s => s !== slug) : [...d, slug],
        );

    const save = async (e: FormEvent) => {
        e.preventDefault();
        setErrors({});
        setStatus(null);
        setBusy(true);
        try {
            await v2(UPDATE_PROFILE, { input: { displayName, username, bio } });
            await v2(SET_DIETARY, { slugs: diet });
            setStatus({
                tone: 'success',
                text: 'Your changes have been saved.',
            });
        } catch (err) {
            if (err instanceof ApiError && err.field) {
                setErrors({ [err.field]: err.message });
            } else {
                setStatus({ tone: 'error', text: messageOf(err) });
            }
        }
        setBusy(false);
    };

    return (
        <form className="flex flex-col gap-10" onSubmit={save} noValidate>
            <Section id="profile" title="Profile">
                <PhotoPicker viewer={viewer} />
                <Field
                    label="Display name"
                    name="name"
                    autoComplete="name"
                    maxLength={100}
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    error={errors.displayName}
                    placeholder={viewer.username}
                    hint="Optional. Until you set one, people see your username."
                />
                <Field
                    label="Username"
                    name="username"
                    autoComplete="username"
                    maxLength={30}
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    error={errors.username}
                    hint="Your public handle: letters, numbers, hyphens and underscores"
                />
                <div className="flex flex-col gap-2">
                    <Label htmlFor="bio">Bio</Label>
                    <textarea
                        id="bio"
                        rows={3}
                        maxLength={MAX_BIO}
                        value={bio}
                        onChange={e => setBio(e.target.value)}
                        aria-invalid={errors.bio ? true : undefined}
                        placeholder="Chasing the best pho in Footscray."
                        className="w-full rounded-xl border border-input bg-background px-4 py-3 text-body-1 text-ink outline-none placeholder:text-ink-muted focus-visible:border-ink focus-visible:ring-1 focus-visible:ring-ink aria-invalid:border-warning"
                    />
                    <div className="flex justify-between text-caption">
                        <span className="text-warning">{errors.bio}</span>
                        <span className="text-ink-muted">
                            {bio.length}/{MAX_BIO}
                        </span>
                    </div>
                </div>
            </Section>

            <Section
                id="dietary"
                title="Dietary preferences"
                description="We'll use these to filter search and recommendations."
            >
                <div className="flex flex-wrap gap-2">
                    {dietaryOptions.map(option => {
                        const on = diet.includes(option.slug);
                        return (
                            <button
                                key={option.slug}
                                type="button"
                                aria-pressed={on}
                                onClick={() => toggle(option.slug)}
                                className={cn(
                                    'inline-flex min-h-9 items-center gap-1.5 rounded-full px-4 text-body-2 transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                                    on
                                        ? 'bg-ink text-white'
                                        : 'bg-surface-subtle text-ink hover:bg-surface-muted',
                                )}
                            >
                                {option.name}
                                {on && (
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        aria-hidden="true"
                                    >
                                        <path d="m5 12 5 5 9-10" />
                                    </svg>
                                )}
                            </button>
                        );
                    })}
                </div>
            </Section>

            <div className="flex flex-col gap-3">
                {status && <Alert tone={status.tone}>{status.text}</Alert>}
                <div className="flex justify-end">
                    <Button type="submit" size="lg" disabled={busy}>
                        {busy ? 'Saving…' : 'Save changes'}
                    </Button>
                </div>
            </div>
        </form>
    );
};

// ─── Password ─────────────────────────────────────────────────────────────────

const PasswordForm = ({ viewer }: { viewer: SettingsViewer }) => {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [status, setStatus] = useState<{
        tone: 'error' | 'success';
        text: string;
    } | null>(null);
    const [busy, setBusy] = useState(false);

    if (!viewer.hasPassword) {
        const sendLink = async () => {
            setBusy(true);
            setStatus(null);
            try {
                await v2(FORGOT, { email: viewer.email });
                setStatus({
                    tone: 'success',
                    text: `We've emailed ${viewer.email} a link to set a password.`,
                });
            } catch (err) {
                setStatus({ tone: 'error', text: messageOf(err) });
            }
            setBusy(false);
        };
        return (
            <Section
                id="password"
                title="Password"
                description="You sign in with Google. Add a password to also sign in with your email."
            >
                {status && <Alert tone={status.tone}>{status.text}</Alert>}
                <div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={sendLink}
                        disabled={busy}
                    >
                        Email me a link to set a password
                    </Button>
                </div>
            </Section>
        );
    }

    const save = async (e: FormEvent) => {
        e.preventDefault();
        setErrors({});
        setStatus(null);
        if (next.length < 8) {
            setErrors({
                newPassword: 'Password must be at least 8 characters',
            });
            return;
        }
        setBusy(true);
        try {
            await v2(CHANGE_PASSWORD, {
                currentPassword: current,
                newPassword: next,
            });
            setCurrent('');
            setNext('');
            setStatus({
                tone: 'success',
                text: 'Password changed. Your other devices have been signed out.',
            });
        } catch (err) {
            if (err instanceof ApiError && err.field)
                setErrors({ [err.field]: err.message });
            else setStatus({ tone: 'error', text: messageOf(err) });
        }
        setBusy(false);
    };

    return (
        <Section
            id="password"
            title="Password"
            description="Changing it signs you out on your other devices."
        >
            <form className="flex flex-col gap-4" onSubmit={save} noValidate>
                <PasswordField
                    label="Current password"
                    name="current-password"
                    autoComplete="current-password"
                    value={current}
                    onChange={e => setCurrent(e.target.value)}
                    error={errors.currentPassword}
                />
                <div className="flex flex-col gap-3">
                    <PasswordField
                        label="New password"
                        name="new-password"
                        autoComplete="new-password"
                        maxLength={128}
                        value={next}
                        onChange={e => setNext(e.target.value)}
                        error={errors.newPassword}
                    />
                    <StrengthMeter password={next} />
                </div>
                {status && <Alert tone={status.tone}>{status.text}</Alert>}
                <div>
                    <Button
                        type="submit"
                        variant="secondary"
                        disabled={busy || !current || !next}
                    >
                        {busy ? 'Updating…' : 'Update password'}
                    </Button>
                </div>
            </form>
        </Section>
    );
};

// ─── Sessions + delete ────────────────────────────────────────────────────────

const SessionsSection = () => {
    const [error, setError] = useState('');
    const logOutEverywhere = async () => {
        setError('');
        try {
            await v2(LOGOUT_EVERYWHERE);
            clearSignedInHint();
            go('/login');
        } catch (err) {
            setError(messageOf(err));
        }
    };
    return (
        <Section
            id="sessions"
            title="Signed-in devices"
            description="Lost a phone or used a shared computer? Sign out everywhere, including here."
        >
            {error && <Alert tone="error">{error}</Alert>}
            <div>
                <Button
                    type="button"
                    variant="outline"
                    onClick={logOutEverywhere}
                >
                    Log out of all devices
                </Button>
            </div>
        </Section>
    );
};

const DeleteSection = ({ viewer }: { viewer: SettingsViewer }) => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const remove = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        setBusy(true);
        try {
            await v2(DELETE_ACCOUNT, {
                password: viewer.hasPassword ? password : null,
            });
            clearSignedInHint();
            go('/');
        } catch (err) {
            setError(messageOf(err));
            setBusy(false);
        }
    };

    return (
        <section id="delete" className="flex scroll-mt-24 flex-col gap-4">
            <div className="flex flex-col gap-1">
                <h2 className="text-title-3 font-semibold text-destructive">
                    Delete account
                </h2>
                <p className="text-body-2 text-ink-muted">
                    Removes your profile, preferences and sign-in details. This
                    can&apos;t be undone.
                </p>
            </div>
            <Dialog>
                <DialogTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        className="self-start border-destructive text-destructive"
                    >
                        Delete my account
                    </Button>
                </DialogTrigger>
                <DialogContent>
                    <form onSubmit={remove}>
                        <DialogHeader>
                            <DialogTitle>Delete your account?</DialogTitle>
                        </DialogHeader>
                        <DialogBody className="flex flex-col gap-4">
                            <DialogDescription>
                                Your profile and preferences will be removed and
                                you&apos;ll be signed out everywhere. This
                                can&apos;t be undone.
                            </DialogDescription>
                            {viewer.hasPassword && (
                                <PasswordField
                                    label="Enter your password to confirm"
                                    name="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                />
                            )}
                            {error && <Alert tone="error">{error}</Alert>}
                        </DialogBody>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="naked">
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                type="submit"
                                variant="destructive"
                                disabled={
                                    busy || (viewer.hasPassword && !password)
                                }
                            >
                                {busy ? 'Deleting…' : 'Delete account'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </section>
    );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const SettingsView = () => {
    const [load, setLoad] = useState<Load>({ status: 'loading' });

    useEffect(() => {
        let cancelled = false;
        loadSettings()
            .then(data => {
                if (cancelled) return;
                if (!data) {
                    clearSignedInHint();
                    toLogin();
                    return;
                }
                setLoad({ status: 'ready', ...data });
            })
            .catch(err => {
                if (cancelled) return;
                if (err instanceof ApiError && err.code === 'UNAUTHENTICATED') {
                    clearSignedInHint();
                    toLogin();
                    return;
                }
                setLoad({ status: 'error', message: messageOf(err) });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className="tw mx-auto flex w-full max-w-[1142px] flex-col gap-8 px-4 py-10 md:px-8">
            <h1 className="text-title-1 font-semibold text-ink">
                Account settings
            </h1>
            {load.status === 'loading' && (
                <p className="text-body-2 text-ink-muted">
                    Loading your account…
                </p>
            )}
            {load.status === 'error' && (
                <Alert tone="error">{load.message}</Alert>
            )}
            {load.status === 'ready' && (
                <div className="grid gap-10 md:grid-cols-[240px_minmax(0,1fr)]">
                    <nav
                        aria-label="Settings sections"
                        className="flex flex-col gap-1 md:sticky md:top-24 md:self-start"
                    >
                        {SECTIONS.map(([id, label]) => (
                            <a
                                key={id}
                                href={`#${id}`}
                                className="rounded-full px-4 py-2 text-body-2 text-ink no-underline hover:bg-surface-subtle"
                            >
                                {label}
                            </a>
                        ))}
                        <span className="my-2 h-px bg-border-subtle" />
                        <a
                            href="#delete"
                            className="rounded-full px-4 py-2 text-body-2 text-destructive no-underline hover:bg-surface-subtle"
                        >
                            Delete account
                        </a>
                    </nav>
                    <div className="flex max-w-[712px] flex-col gap-10">
                        <ProfileForm
                            viewer={load.viewer}
                            dietaryOptions={load.dietaryOptions}
                        />
                        <PasswordForm viewer={load.viewer} />
                        <SessionsSection />
                        <DeleteSection viewer={load.viewer} />
                    </div>
                </div>
            )}
        </div>
    );
};
